// components/operations/EmployerList.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, Clock } from "lucide-react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateToBritish } from "@/lib/dateUtils";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { EmployerSummary } from "@/types/operations";
import SearchFilter from "./SearchFilter";
import DateRangeFilter from "./DateRangeFilter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EmployerList: React.FC = () => {
  const [employers, setEmployers] = useState<EmployerSummary[]>([]);
  const [filteredEmployers, setFilteredEmployers] = useState<EmployerSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateRange, setDateRange] = useState<{start?: Date, end?: Date}>({});
  const navigate = useNavigate();

  // Fetch employers on component mount
  useEffect(() => {
    const fetchEmployers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await OperationsDashboardService.getEmployerList();
        setEmployers(data);
        setFilteredEmployers(data);
      } catch (err) {
        setError("Failed to load employers. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployers();
  }, []);

  // Apply all filters
  useEffect(() => {
    let filtered = [...employers];
    
    // Filter out demo accounts (emails containing "demo-employer")
    filtered = filtered.filter(employer => 
      !employer.employer_email.includes("demo-employer")
    );
    
    // Filter out accounts created before May 20th, 2025
    // Note: We'll use latest_payrun_date as a proxy for account activity since we don't have creation date
    // This is not perfect but it's the best we can do with available data
    const cutoffDate = new Date('2025-05-20');
    filtered = filtered.filter(employer => {
      if (!employer.latest_payrun_date) return true; // Keep accounts with no payrun data
      const payrunDate = new Date(employer.latest_payrun_date);
      return payrunDate >= cutoffDate;
    });
    
    // Apply search filter
    if (searchQuery.trim()) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (employer) =>
          employer.employer_email.toLowerCase().includes(lowercaseQuery) ||
          employer.employee_name.toLowerCase().includes(lowercaseQuery)
      );
    }
    
    // Apply date filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((employer) => {
        if (!employer.latest_payrun_date) return false;
        
        const payrunDate = new Date(employer.latest_payrun_date);
        return payrunDate >= dateRange.start! && payrunDate <= dateRange.end!;
      });
    }
    
    setFilteredEmployers(filtered);
  }, [employers, searchQuery, dateRange]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle date range filter
  const handleDateRangeChange = (start: Date | undefined, end: Date | undefined) => {
    setDateRange({ start, end });
  };

  // Navigate to employer detail page
  const handleEmployerClick = (employerId: number) => {
    navigate(`/operations/employers/${employerId}`);
  };

  // Format payrun status with appropriate styling
  const getStatusBadge = (status: string | undefined) => {
    if (!status) return null;
    
    let variant = "";
    switch (status.toLowerCase()) {
      case "approved":
        variant = "bg-green-100 text-green-800";
        break;
      case "pending":
        variant = "bg-yellow-100 text-yellow-800";
        break;
      case "draft":
        variant = "bg-gray-100 text-gray-800";
        break;
      case "error":
        variant = "bg-red-100 text-red-800";
        break;
      default:
        variant = "bg-blue-100 text-blue-800";
    }
    
    return <Badge className={variant}>{status}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-gray-500">Loading employers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
          <p className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle>Employer List</CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <DateRangeFilter onFilterChange={handleDateRangeChange} />
            <SearchFilter 
              onSearch={handleSearch} 
              placeholder="Search employers..." 
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employer Email</TableHead>
              <TableHead>Employee Name</TableHead>
              <TableHead className="hidden md:table-cell">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Latest Payrun
                </div>
              </TableHead>
              <TableHead className="hidden md:table-cell">Status</TableHead>
              <TableHead className="text-right">Issues</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                  No employers found
                </TableCell>
              </TableRow>
            ) : (
              filteredEmployers.map((employer) => (
                <TableRow 
                  key={employer.employer_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleEmployerClick(employer.employer_id)}
                >
                  <TableCell className="font-medium">{employer.employer_email}</TableCell>
                  <TableCell>{employer.employee_name}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {employer.latest_payrun_date 
                      ? formatDateToBritish(employer.latest_payrun_date)
                      : "No payrun yet"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {getStatusBadge(employer.latest_payrun_status)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center space-x-2">
                      {employer.critical_issues > 0 && (
                        <Badge variant="destructive" className="flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {employer.critical_issues}
                        </Badge>
                      )}
                      {employer.warning_issues > 0 && (
                        <Badge variant="outline" className="flex items-center border-yellow-500 text-yellow-700">
                          <Clock className="w-3 h-3 mr-1" />
                          {employer.warning_issues}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmployerClick(employer.employer_id);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default EmployerList; 